import { observer } from 'mobx-react-lite';
import { IconButton } from '@fluentui/react/lib/Button';
import { Icon } from '@fluentui/react/lib/Icon';
import { Modal } from '@fluentui/react/lib/Modal';
import { ControlIcons } from 'owa-control-icons';
import {
    FilePickerAction,
    FilePickerActionsPanelButtonView,
    lazyGetShareIconForActionPanel,
    SelectedFilesSource,
} from 'owa-filepicker';
import {
    FileProviderType,
    FileProviderViewState,
    getProviderBrandName,
} from 'owa-fileprovider-store';
import loc, { format } from 'owa-localize';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import type { SonoraViewState } from 'owa-editor-sonora-types';
import { showActionsPanel, hideActionsPanel } from '../mutators/sonoraClassicAttachmentMutators';
import {
    SonoraActionsPanel_UploadAndShareLink,
    SonoraActionsPanel_UploadAndShareLinkTextDescriptionText,
    SonoraActionsPanel_DefaultUploadFolderName,
    SonoraActionsPanel_AttachAsACopyText,
    SonoraActionsPanel_AttachAsACopyDescriptionText,
    SonoraActionsPanel_Text,
    SonoraActionsPanel_CloseButtonText,
} from './SonoraClassicAttachmentActionsPanel.locstring.json';

const ACTIONS_PANEL_DIV_ID = 'SonoraClassicAttachmentActionsPanelDiv';

interface SonoraClassicAttachmentActionsPanelProps {
    onActionSelected: (action: FilePickerAction) => void;
    onCancelCallback: () => void;
    viewState: SonoraViewState;
    supportedActions: FilePickerAction[];
    fileProvider: FileProviderViewState;
    getShareIconForActionPanel: (
        filesSource: SelectedFilesSource,
        providerType: FileProviderType,
        isDisabled: boolean
    ) => JSX.Element;
}

import styles from 'owa-filepicker/lib/components/ActionsPanel/FilePickerActionsPanelView.scss';

// This component is responsible for showing the share options for the user.
const SonoraClassicAttachmentActionsPanel = observer(function SonoraClassicAttachmentActionsPanel(
    props: SonoraClassicAttachmentActionsPanelProps
) {
    const {
        onActionSelected,
        onCancelCallback,
        viewState,
        supportedActions,
        fileProvider,
        getShareIconForActionPanel,
    } = props;
    const [selectedAction, setSelectedAction] = React.useState<FilePickerAction | undefined>(
        undefined
    );
    const renderFilePickerActionsPanelContent = () => {
        const isShareSupported = supportedActions.some(action => action === FilePickerAction.Share);
        const isAttachSupported = supportedActions.some(
            action => action === FilePickerAction.Attach
        );
        const isInsertAsLinkSupported = supportedActions.some(
            action => action === FilePickerAction.InsertAsLink
        );

        const providerDisplayName = getProviderBrandName(fileProvider.type);
        const shareButtonIcon: JSX.Element = isInsertAsLinkSupported ? (
            <Icon iconName={ControlIcons.Cloud} className={styles.icon} />
        ) : (
            getShareIconForActionPanel(
                SelectedFilesSource.ProviderFiles,
                fileProvider.type,
                !isShareSupported
            )
        );
        const shareButtonComponent = (
            <FilePickerActionsPanelButtonView
                action={FilePickerAction.Share}
                actionLabel={format(
                    loc(SonoraActionsPanel_UploadAndShareLink),
                    providerDisplayName
                )}
                actionDescription={format(
                    loc(SonoraActionsPanel_UploadAndShareLinkTextDescriptionText),
                    loc(SonoraActionsPanel_DefaultUploadFolderName)
                )}
                buttonIcon={shareButtonIcon}
                isDisabled={!isShareSupported}
                onActionButtonClicked={onActionButtonClicked}
                shouldAutoFocus={!isInsertAsLinkSupported && isShareSupported}
            />
        );
        const attachButtonComponent = (
            <FilePickerActionsPanelButtonView
                action={FilePickerAction.Attach}
                actionLabel={loc(SonoraActionsPanel_AttachAsACopyText)}
                actionDescription={loc(SonoraActionsPanel_AttachAsACopyDescriptionText)}
                buttonIcon={<Icon iconName={ControlIcons.Attach} className={styles.icon} />}
                isDisabled={!isAttachSupported}
                onActionButtonClicked={onActionButtonClicked}
                shouldAutoFocus={!isInsertAsLinkSupported && !isShareSupported && isAttachSupported}
            />
        );

        let buttons = [shareButtonComponent, attachButtonComponent];

        if (isInsertAsLinkSupported) {
            // Todo: finalize strings for share as a link when the action is supported.
            const insertButtonComponent = isInsertAsLinkSupported && (
                <FilePickerActionsPanelButtonView
                    action={FilePickerAction.InsertAsLink}
                    actionLabel={format(
                        loc(SonoraActionsPanel_UploadAndShareLink),
                        providerDisplayName
                    )}
                    actionDescription={format(
                        loc(SonoraActionsPanel_UploadAndShareLinkTextDescriptionText),
                        providerDisplayName
                    )}
                    buttonIcon={<Icon iconName={ControlIcons.Link} className={styles.icon} />}
                    isDisabled={!isShareSupported}
                    onActionButtonClicked={onActionButtonClicked}
                    shouldAutoFocus={!isAttachSupported && isShareSupported}
                />
            );
            buttons.unshift(insertButtonComponent);
        }

        return (
            <>
                <div className={styles.title}>{loc(SonoraActionsPanel_Text)}</div>
                <div className={styles.closeButton}>
                    <IconButton
                        aria-label={loc(SonoraActionsPanel_CloseButtonText)}
                        iconProps={{
                            iconName: ControlIcons.Cancel,
                        }}
                        onClick={closePanel}
                    />
                </div>
                {buttons}
            </>
        );
    };

    const onActionButtonClicked = (action: FilePickerAction) => {
        setSelectedAction(action);
        closePanel();
    };
    const closePanel = () => {
        hideActionsPanel(viewState);
    };
    const onPanelDismissed = () => {
        if (selectedAction !== undefined) {
            onActionSelected(selectedAction);
            setSelectedAction(undefined);
        } else {
            onCancelCallback();
        }

        // Unmount the element and remove it from DOM
        const targetWindow = window;
        const actionsPanelDiv = targetWindow.document.getElementById(ACTIONS_PANEL_DIV_ID);
        removeHTMLElementFromDOM(actionsPanelDiv);
    };

    const divRef = React.useRef<HTMLDivElement>(null);
    const focusActionsPanel = () => {
        divRef.current!.tabIndex = 0;
        divRef.current!.focus();
    };
    const focusOutActionsPanel = () => {
        divRef.current!.tabIndex = -1;
    };
    return (
        <Modal
            isOpen={viewState.isClassicAttachmentActionPanelOpen}
            onDismiss={closePanel}
            onDismissed={onPanelDismissed}
            forceFocusInsideTrap={true}>
            <div
                ref={divRef}
                className={styles.container}
                onClick={focusActionsPanel}
                onBlur={focusOutActionsPanel}>
                {renderFilePickerActionsPanelContent()}
            </div>
        </Modal>
    );
});

/**
 * The showSonoraClassicAttachmentActionsPanel is responsible for creating an element and then rendering the
 * SonoraClassicAttachmentActionsPanel in it.
 * The reason to do that is to make this component simpler from a consumer perspective so that
 * they don't have to worry about creating the component themselves and maintaining show/close lifecycle.
 * Instead they create the panel and once the action is selected and the panel is dismissed they will get a
 * callback containing the user choice.
 */

export default async function showSonoraClassicAttachmentActionsPanel(
    resolve: (action: FilePickerAction) => void,
    reject: () => void,
    viewState: SonoraViewState,
    supportedActions: FilePickerAction[],
    fileProvider: FileProviderViewState
): Promise<void> {
    // With projection popout we can have several actions panel divs at the same time, so we must give them unique ids.
    const panelDiv = document.createElement('div');
    panelDiv.id = ACTIONS_PANEL_DIV_ID;
    document.body.appendChild(panelDiv);

    const getShareIconForActionPanel = await lazyGetShareIconForActionPanel.import();

    ReactDOM.render(
        <React.StrictMode>
            <SonoraClassicAttachmentActionsPanel
                onActionSelected={resolve}
                onCancelCallback={reject}
                viewState={viewState}
                supportedActions={supportedActions}
                fileProvider={fileProvider}
                getShareIconForActionPanel={getShareIconForActionPanel}
            />
        </React.StrictMode>,
        panelDiv
    );

    showActionsPanel(viewState);
}

export function removeHTMLElementFromDOM(HTMLElement: HTMLElement | null) {
    if (HTMLElement) {
        ReactDOM.unmountComponentAtNode(HTMLElement);
        if (HTMLElement.parentNode) {
            HTMLElement.parentNode.removeChild(HTMLElement);
        }
    }
}
